"use client";

import React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "@/shared/utils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { PublicConfig } from "@/shared/PublicConfig";
import Strings from "@/shared/utils/Strings.constants";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { Quote } from "@/shared/types/Quote.interface";
import { useStatsig } from "@/shared/hooks/useStatsig";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useBillingFrequency, BillingFrequencyType } from "@/shared/hooks/useBillingFrequency";
import { RadioButtonGroup } from "@/shared/components/ui/RadioButtonGroup";

// media
import DollarIcon from "../../media/icons/dollars-icon.svg";
import { faCircleCheck, faCircleDollar } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck as faCircleCheckSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const COPY = Strings.PTZ_US.ANNUAL_BILLING;

interface BillingFrequencyFieldProps {
    quote: Quote;
    /** RadioButtonGroup styling variant, control arm only. */
    variant?: "floating" | "floating-inner";
}

/** 16px radio glyph: grey-ringed when off, brand ring + brand dot when on. */
const RadioDot = ({ checked }: { checked: boolean }) => (
    <span
        aria-hidden="true"
        className={cn(
            "flex size-4 shrink-0 items-center justify-center rounded-full bg-background-primary",
            checked ? "border-2 border-stroke-brand" : "border border-stroke-primary"
        )}
    >
        <RadioGroupPrimitive.Indicator className="block size-2 rounded-full bg-background-brand-primary" />
    </span>
);

/** Bold amount with a smaller cadence suffix ("$102.00" + "/mo"). */
const Price = ({ amount, suffix, className, suffixClassName }: { amount: number; suffix: string; className?: string; suffixClassName?: string }) => (
    <span className={cn("flex items-center whitespace-nowrap", className)}>
        <span className="text-base font-bold leading-5">{UIUtils.formatCurrency(amount)}</span>
        <span className={cn("text-xs font-medium leading-4", suffixClassName)}>{suffix}</span>
    </span>
);

/**
 * Billing-schedule input for the Billing step.
 *
 * Owns the ENG-18612 exposure. It lives here rather than in BillingEditor/formController on purpose:
 * BillingEditor is also rendered by the add-a-pet form with `hideBillingFrequency`, and reading the
 * layer there would log an exposure for customers who never see a billing-schedule input at all.
 * Because this component is only mounted when the input actually renders, exposure and experience match.
 *
 * Control keeps the segmented Billed Monthly / Billed Annually toggle. Both variants replace it with a
 * radio group that prices both schedules at once; they differ only in layout — `side-by-side` is a 2-up
 * grid (Figma 80847:31104), `stacked` two full-width rows (Figma 80847:30473).
 */
export const BillingFrequencyField = ({ quote, variant = "floating-inner" }: BillingFrequencyFieldProps) => {
    const { appState } = useAppLayerContext();
    const { isQuoteUpdating, showButtonStyle } = appState;

    // Statsig - Annual billing opt-in gravitas (ENG-18612). Three equal arms; anything other than the
    // two variant strings (including "" and a stale value) renders control.
    const { showAnnualBillingGravitas } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showAnnualBillingGravitas", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    const { selectedBillingFrequency, frequencyToDisplay, showTransactionFee, annualSavings, monthlyTotal, annualTotal, annualTotalIfBilledMonthly, handleFrequencyChange } =
        useBillingFrequency(quote);

    const isStacked = showAnnualBillingGravitas === "stacked";
    const showRadioCards = isStacked || showAnnualBillingGravitas === "side-by-side";
    const isAnnualSelected = selectedBillingFrequency === "yearly";
    const formattedSavings = UIUtils.formatCurrency(annualSavings, { min: 0, max: 2 });

    const renderOption = (frequency: BillingFrequencyType) => {
        const isMonthly = frequency === "monthly";
        const checked = selectedBillingFrequency === frequency;

        // Stacked pins the price block to the height of its two-line form (36px, per Figma) so the
        // one-line monthly row and the two-line annual row stay the same height. Side-by-side needs no
        // pin: the grid already stretches both cards to match.
        // Same two elements in both layouts, opposite order: side-by-side leads with the live price,
        // stacked leads with the struck one (Figma 80847:31130 vs 80847:30477).
        const strikethrough = !isMonthly && showTransactionFee && (
            <span className="text-xs font-medium leading-4 text-content-secondary line-through">{UIUtils.formatCurrency(annualTotalIfBilledMonthly)}</span>
        );
        const priceBlock = (
            <span className={cn("flex flex-col", isStacked ? "h-9 items-end justify-center" : "items-start")}>
                {isStacked && strikethrough}
                {isMonthly ? (
                    <Price amount={monthlyTotal} suffix="/mo" className="text-content-primary" suffixClassName={isStacked ? "text-content-secondary" : undefined} />
                ) : (
                    <Price amount={annualTotal} suffix="/yr" className="text-content-success" />
                )}
                {!isStacked && strikethrough}
            </span>
        );

        return (
            <RadioGroupPrimitive.Item
                key={frequency}
                value={frequency}
                // Radix rolls the group up into a single tab stop (arrows move between options). Make
                // every option its own tab stop so Tab / Shift+Tab walk both of them too.
                tabIndex={0}
                data-testid={`billing-frequency-option-${frequency}`}
                className={cn(
                    "rounded-lg border border-stroke-success-light text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-brand-b focus-visible:ring-offset-1",
                    isQuoteUpdating ? "cursor-wait" : "cursor-pointer",
                    isStacked ? "w-full" : "h-full"
                )}
            >
                <span
                    className={cn(
                        "flex h-full rounded-lg border-2",
                        checked ? "border-stroke-brand-b bg-background-primary" : "bg-background-secondary",
                        !checked && (isStacked ? "border-stroke-secondary" : "border-stroke-transparent"),
                        isStacked ? "items-center gap-4 px-4 py-3 shadow-sm" : "flex-col gap-0.5 p-3"
                    )}
                >
                    {isStacked && <RadioDot checked={checked} />}
                    {isStacked ? (
                        <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                            <span className="text-sm font-bold leading-5 text-content-primary">{isMonthly ? COPY.MONTHLY_LABEL : COPY.ANNUAL_LABEL}</span>
                            {priceBlock}
                        </span>
                    ) : (
                        <React.Fragment>
                            <span className="flex w-full items-center justify-between gap-2">
                                <span className="text-sm font-medium leading-5 text-content-secondary">{isMonthly ? COPY.MONTHLY_LABEL : COPY.ANNUAL_LABEL}</span>
                                <RadioDot checked={checked} />
                            </span>
                            {priceBlock}
                        </React.Fragment>
                    )}
                </span>
            </RadioGroupPrimitive.Item>
        );
    };

    return (
        // The variants are always framed: the green container is the card these two options live in
        // (Figma 80847:31105 / 80847:30474), not a savings badge, so it must not blink out with the
        // transaction fee. Only the figures that state a saving — the struck price and the callout —
        // stay fee-gated. Control keeps its existing fee-gated framing so the arms stay comparable.
        <div data-testid="billing-frequency-section" className={cn((showRadioCards || showTransactionFee) && "rounded-xl bg-background-success px-3 pb-1 pt-2")}>
            {showRadioCards ? (
                <React.Fragment>
                    <RadioGroupPrimitive.Root
                        value={selectedBillingFrequency}
                        onValueChange={async value => await handleFrequencyChange(value as BillingFrequencyType)}
                        // Radix suppresses Enter on radio items (native radios only select with Space) and
                        // discards an item-level onKeyDown, so pick the focused option on Enter from here.
                        onKeyDown={event => {
                            if (event.key === "Enter" && event.target instanceof HTMLElement && event.target.getAttribute("role") === "radio") event.target.click();
                        }}
                        // Not `disabled` while the quote updates: a focused button that turns disabled drops
                        // keyboard focus to <body>, so Tab / Shift+Tab lose their place in the group. The
                        // handler already ignores changes made mid-update; aria-busy + cursor-wait carry the state.
                        aria-busy={isQuoteUpdating}
                        // Radix makes the group wrapper the tab stop and, on entry, forwards focus to the
                        // checked option, so Tab always skipped the unchecked one. Take the wrapper out of the
                        // tab order; each option is its own tab stop (tabIndex 0 below) whatever is checked.
                        tabIndex={-1}
                        aria-label="Billing schedule"
                        data-testid="annual-billing-radio-group"
                        data-layout={showAnnualBillingGravitas}
                        className={cn(isStacked ? "flex flex-col gap-2" : "grid grid-cols-2 items-stretch gap-3")}
                    >
                        {renderOption("monthly")}
                        {renderOption("yearly")}
                    </RadioGroupPrimitive.Root>
                    {showTransactionFee && (
                        <div className="flex flex-row items-center justify-center gap-1 pt-2 text-content-success" data-testid="billing-frequency-savings">
                            <span className="flex size-5 items-center justify-center">
                                <FontAwesomeIcon icon={isAnnualSelected ? faCircleCheckSolid : faCircleDollar} className="size-4" />
                            </span>
                            <span className="text-sm leading-5">
                                {isAnnualSelected ? (
                                    <strong className="font-bold">{COPY.SAVINGS_CONFIRMED.replace("%amount%", formattedSavings)}</strong>
                                ) : (
                                    <React.Fragment>
                                        <strong className="font-bold">{COPY.SAVINGS_PROMPT_EMPHASIS.replace("%amount%", formattedSavings)}</strong>
                                        <span className="font-medium">{COPY.SAVINGS_PROMPT_TAIL}</span>
                                    </React.Fragment>
                                )}
                            </span>
                        </div>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <RadioButtonGroup
                        variant={variant}
                        key={selectedBillingFrequency}
                        onValueChange={async value => await handleFrequencyChange(value as BillingFrequencyType)}
                        initialValue={selectedBillingFrequency}
                        aria-labelledby="billing-frequency"
                        className={cn("bg-background-weakest", showTransactionFee && "rounded-xl border border-stroke-success-light")}
                        disabled={isQuoteUpdating}
                        radioGroupItemClassName={
                            showTransactionFee
                                ? cn(
                                      "!border-0 data-[state=on]:!border-2",

                                      showButtonStyle ? "data-[state=on]:!border-stroke-secondary-invert" : "data-[state=on]:!border-stroke-brand-b"
                                  )
                                : "data-[state=off]:!border data-[state=off]:!border-stroke-primary"
                        }
                        options={[
                            { label: "Billed Monthly", value: "monthly" },
                            { label: "Billed Annually", value: "yearly" }
                        ]}
                    />
                    {showTransactionFee && (
                        <div className="flex flex-row items-center justify-center gap-1 pt-2">
                            {frequencyToDisplay === "yearly" ? <FontAwesomeIcon icon={faCircleCheck} className="size-5 text-content-primary" /> : <DollarIcon />}
                            <span className="text-xs font-semibold text-content-primary">
                                {frequencyToDisplay === "yearly" ? `You're saving $${annualSavings}/yr!` : `Save $${annualSavings}/yr with annual billing.`}
                            </span>
                        </div>
                    )}
                </React.Fragment>
            )}
        </div>
    );
};

export default BillingFrequencyField;
